import { apiRequest, ApiResponse } from './api';
import { Ticket } from '../types';

export interface TicketFilters {
  status?: 'aberto' | 'em_andamento' | 'resolvido' | 'fechado' | 'cancelado' | 'todos';
  prioridade?: 'baixa' | 'normal' | 'alta' | 'urgente' | 'todos';
  chamado_assunto_id?: number | string | 'todos';
  atribuido_para?: number;
  aberto_por?: number;
  cliente_id?: number;
  search?: string;
  data_inicio?: string;
  data_fim?: string;
}

export interface TicketListParams {
  page?: number;
  limit?: number;
  filters?: TicketFilters;
}

export interface ChamadoRankingCliente {
  id: number;
  nome_cliente: string;
  numero: string | null;
  ddi: string | null;
  chamados: number;
}

export interface ChamadoRankingTecnico {
  id: number;
  nome_tecnico: string;
  cargo: string;
  celular: string | null;
  chamados: number;
}

export interface ChamadoRankingAssunto {
  id: number;
  nome_assunto: string;
  nome_categoria: string | null;
  chamados: number;
}

class TicketService {
  private baseUrl = '/tickets';

  // Listar todos os tickets (admin, financeiro, suporte)
  async getAll(params: TicketListParams = {}): Promise<ApiResponse<Ticket[]>> {
    const { page = 1, limit = 50, filters = {} } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Adicionar filtro de status (incluindo 'todos' para retornar todos os status)
    if (filters.status) {
      queryParams.append('status', filters.status);
    }
    
    if (filters.prioridade && filters.prioridade !== 'todos') {
      queryParams.append('prioridade', filters.prioridade);
    }

    if (filters.chamado_assunto_id && filters.chamado_assunto_id !== 'todos') {
      queryParams.append('chamado_assunto_id', String(filters.chamado_assunto_id));
    }

    if (filters.atribuido_para) {
      queryParams.append('atribuido_para', String(filters.atribuido_para));
    }

    if (filters.aberto_por) {
      queryParams.append('aberto_por', String(filters.aberto_por));
    }

    if (filters.cliente_id) {
      queryParams.append('cliente_id', String(filters.cliente_id));
    }
    
    if (filters.search && filters.search.trim()) {
      queryParams.append('search', filters.search.trim());
    }
    
    if (filters.data_inicio) {
      queryParams.append('data_inicio', filters.data_inicio);
    }
    
    if (filters.data_fim) {
      queryParams.append('data_fim', filters.data_fim);
    }

    return apiRequest.get<Ticket[]>(`${this.baseUrl}?${queryParams}`);
  }

  async getRanking(params?: { data_inicio?: string; data_fim?: string; chamado_assunto_id?: number }): Promise<ChamadoRankingCliente[]> {
    const searchParams = new URLSearchParams();
    if (params?.data_inicio) searchParams.set('data_inicio', params.data_inicio);
    if (params?.data_fim) searchParams.set('data_fim', params.data_fim);
    if (params?.chamado_assunto_id) searchParams.set('chamado_assunto_id', String(params.chamado_assunto_id));
    const query = searchParams.toString();
    const url = query ? `${this.baseUrl}/ranking?${query}` : `${this.baseUrl}/ranking`;
    const data: any = await apiRequest.get<ChamadoRankingCliente[]>(url);
    if (Array.isArray(data)) return data;
    return data?.data || [];
  }

  async getRankingTecnicos(params?: { data_inicio?: string; data_fim?: string; chamado_assunto_id?: number }): Promise<ChamadoRankingTecnico[]> {
    const searchParams = new URLSearchParams();
    if (params?.data_inicio) searchParams.set('data_inicio', params.data_inicio);
    if (params?.data_fim) searchParams.set('data_fim', params.data_fim);
    if (params?.chamado_assunto_id) searchParams.set('chamado_assunto_id', String(params.chamado_assunto_id));
    const query = searchParams.toString();
    const url = query ? `${this.baseUrl}/ranking-tecnicos?${query}` : `${this.baseUrl}/ranking-tecnicos`;
    const data: any = await apiRequest.get<ChamadoRankingTecnico[]>(url);
    if (Array.isArray(data)) return data;
    return data?.data || [];
  }

  async getRankingAssuntos(params?: { data_inicio?: string; data_fim?: string; atribuido_para?: number }): Promise<ChamadoRankingAssunto[]> {
    const searchParams = new URLSearchParams();
    if (params?.data_inicio) searchParams.set('data_inicio', params.data_inicio);
    if (params?.data_fim) searchParams.set('data_fim', params.data_fim);
    if (params?.atribuido_para) searchParams.set('atribuido_para', String(params.atribuido_para));
    const query = searchParams.toString();
    const url = query ? `${this.baseUrl}/ranking-assuntos?${query}` : `${this.baseUrl}/ranking-assuntos`;
    const data: any = await apiRequest.get<ChamadoRankingAssunto[]>(url);
    if (Array.isArray(data)) return data;
    return data?.data || [];
  }

  // Listar tickets abertos
  async getOpenTickets(): Promise<ApiResponse<Ticket[]>> {
    return apiRequest.get<Ticket[]>(`${this.baseUrl}/open`);
  }

  // Listar tickets de um cliente
  async getByClientId(
    clientId: number,
    params: TicketListParams = {}
  ): Promise<ApiResponse<Ticket[]>> {
    const { page = 1, limit = 10, filters = {} } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, String(value)])
      ),
    });

    return apiRequest.get<Ticket[]>(`${this.baseUrl}/client/${clientId}?${queryParams}`);
  }

  // Obter estatísticas de tickets de um cliente
  async getStatsForClient(clientId: number): Promise<ApiResponse<{
    total: number;
    aberto: number;
    em_andamento: number;
    resolvido: number;
    fechado: number;
    cancelado: number;
  }>> {
    return apiRequest.get(`${this.baseUrl}/client/${clientId}/stats`);
  }

  // Obter tickets recentes de um cliente
  async getRecentByClientId(
    clientId: number,
    limit: number = 5
  ): Promise<ApiResponse<Ticket[]>> {
    return apiRequest.get<Ticket[]>(`${this.baseUrl}/client/${clientId}/recent?limit=${limit}`);
  }

  // Obter ticket por ID
  async getById(id: number): Promise<ApiResponse<Ticket>> {
    return apiRequest.get<Ticket>(`${this.baseUrl}/${id}`);
  }

  // Criar novo ticket
  async create(data: {
    cliente_id: number;
    contrato_id: number;
    contrato_servico_id?: number;
    chamado_assunto_id?: number;
    descricao: string;
    prioridade?: 'baixa' | 'normal' | 'alta' | 'urgente';
    quem_solicitou?: string;
    data_agendamento?: Date | null;
    atribuido_para?: number;
  }): Promise<ApiResponse<Ticket>> {
    return apiRequest.post<Ticket>(this.baseUrl, data);
  }

  // Atualizar ticket
  async update(id: number, data: Partial<{
    cliente_id: number;
    contrato_id: number;
    chamado_assunto_id?: number;
    descricao: string;
    status: 'aberto' | 'em_andamento' | 'resolvido' | 'fechado' | 'cancelado';
    prioridade: 'baixa' | 'normal' | 'alta' | 'urgente';
    contrato_servico_id?: number;
    quem_solicitou?: string;
    data_agendamento?: Date | null;
  }>): Promise<ApiResponse<Ticket>> {
    return apiRequest.put<Ticket>(`${this.baseUrl}/${id}`, data);
  }

  // Deletar ticket
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiRequest.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Retornar ticket
  async retornarTicket(id: number, status: 'aberto', informacoes?: string): Promise<ApiResponse<Ticket>> {
    return apiRequest.patch<Ticket>(`${this.baseUrl}/${id}/retornar`, { status, informacoes });
  }

  // Alterar status do ticket
  async updateStatus(
    id: number,
    status: 'aberto' | 'em_andamento' | 'resolvido' | 'fechado' | 'cancelado'
  ): Promise<ApiResponse<Ticket>> {
    return this.update(id, { status });
  }

  // Fechar ticket
  async close(id: number, informacoes?: string): Promise<ApiResponse<Ticket>> {
    return apiRequest.patch<Ticket>(`${this.baseUrl}/${id}/close`, { informacoes });
  }

  // Cancelar ticket
  async cancel(id: number, informacoes?: string): Promise<ApiResponse<Ticket>> {
    return apiRequest.patch<Ticket>(`${this.baseUrl}/${id}/cancel`, { informacoes });
  }

  // Atribuir ticket a um funcionário
  async assign(id: number, funcionarioId: number | null): Promise<ApiResponse<Ticket>> {
    return apiRequest.patch<Ticket>(`${this.baseUrl}/${id}/assign`, { funcionarioId });
  }

  // Atualizar data/hora de agendamento do ticket
  async updateSchedule(id: number, data_agendamento: string | Date | null): Promise<ApiResponse<Ticket>> {
    return apiRequest.patch<Ticket>(`${this.baseUrl}/${id}/agendamento`, {
      data_agendamento: data_agendamento instanceof Date ? data_agendamento.toISOString() : data_agendamento,
    });
  }

  // Formatar dados para exibição
  formatTicketForDisplay(ticket: Ticket) {
    return {
      ...ticket,
      status_label: this.getStatusLabel(ticket.status),
      prioridade_label: this.getPrioridadeLabel(ticket.prioridade),
      status_color: this.getStatusColor(ticket.status),
      prioridade_color: this.getPrioridadeColor(ticket.prioridade),
    };
  }

  // Obter label do status
  private getStatusLabel(status: string): string {
    const labels = {
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      resolvido: 'Resolvido',
      fechado: 'Fechado',
      cancelado: 'Cancelado',
    };
    return labels[status as keyof typeof labels] || status;
  }

  // Obter label da prioridade
  private getPrioridadeLabel(prioridade: string): string {
    const labels = {
      baixa: 'Baixa',
      normal: 'Normal',
      alta: 'Alta',
      urgente: 'Urgente',
    };
    return labels[prioridade as keyof typeof labels] || prioridade;
  }

  // Obter cor do status
  private getStatusColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
    const colors = {
      aberto: 'error' as const,
      em_andamento: 'warning' as const,
      resolvido: 'success' as const,
      fechado: 'default' as const,
      cancelado: 'default' as const,
    };
    return colors[status as keyof typeof colors] || 'default';
  }

  // Obter cor da prioridade
  private getPrioridadeColor(prioridade: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
    const colors = {
      baixa: 'success' as const,
      normal: 'info' as const,
      alta: 'warning' as const,
      urgente: 'error' as const,
    };
    return colors[prioridade as keyof typeof colors] || 'default';
  }
}

export const ticketService = new TicketService();
export default ticketService; 